import { useState } from "react";
import { Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inp = "w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-3 py-2 rounded text-white text-sm";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs tracking-widest uppercase text-neutral-400">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function UploadRow({ label, field, form, setForm, upload, uploading, accept }) {
  return (
    <Field label={label}>
      <div className="flex gap-2">
        <input value={form[field] || ""} onChange={(e) => setForm({ ...form, [field]: e.target.value })} placeholder="URL or upload..." className={inp} />
        <label className="btn-ghost-gold px-3 py-2 rounded text-xs tracking-widest uppercase cursor-pointer inline-flex items-center gap-2 whitespace-nowrap">
          {uploading === field ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          Upload
          <input type="file" accept={accept} className="hidden" onChange={(e) => upload(field, e.target.files?.[0])} />
        </label>
      </div>
    </Field>
  );
}

export default function ProductForm({ form, setForm, onSave, onClose, categories }) {
  const [uploading, setUploading] = useState(null);
  const cats = categories[form.type] || categories.music;
  const setType = (newType) => setForm({ ...form, type: newType, category: categories[newType][0] });

  const upload = async (field, file) => {
    if (!file) return;
    setUploading(field);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/admin/upload`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) throw new Error("upload failed");
      const data = await res.json();
      const url = data.url.startsWith("http") ? data.url : `${BACKEND_URL}${data.url}`;
      setForm({ ...form, [field]: url });
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(null); }
  };

  const csv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto" data-testid="product-form-modal">
      <div className="luxury-card rounded-xl w-full max-w-3xl my-8 p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-serif-display text-3xl text-white">{form.id ? "Edit" : "New"} Product</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={20} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Title"><input data-testid="pf-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inp} /></Field>
            <Field label="Type">
              <select data-testid="pf-type" value={form.type} onChange={(e) => setType(e.target.value)} className={inp}>
                <option value="music">Music</option>
                <option value="template">Power BI Template</option>
                <option value="ebook">E-book</option>
                <option value="merch">Merchandise</option>
              </select>
            </Field>
            <Field label="Category">
              <select data-testid="pf-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inp}>
                {cats.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select data-testid="pf-status" value={form.status || "online"} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inp}>
                <option value="online">Online — public</option>
                <option value="offline">Offline — hidden</option>
                <option value="draft">Draft — work in progress</option>
              </select>
            </Field>
            <Field label="Featured?">
              <label className="flex items-center gap-2 mt-2">
                <input data-testid="pf-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
                <span className="text-sm text-neutral-300">Show on homepage</span>
              </label>
            </Field>
          </div>
          <Field label="Description">
            <textarea data-testid="pf-description" required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inp} />
          </Field>

          {form.type === "music" ? (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Price MP3 (£)"><input data-testid="pf-price-mp3" type="number" step="0.01" value={form.price_mp3 || 0} onChange={(e) => setForm({ ...form, price_mp3: parseFloat(e.target.value) })} className={inp} /></Field>
              <Field label="Price WAV (£)"><input data-testid="pf-price-wav" type="number" step="0.01" value={form.price_wav || 0} onChange={(e) => setForm({ ...form, price_wav: parseFloat(e.target.value) })} className={inp} /></Field>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Price (£)"><input data-testid="pf-price" type="number" step="0.01" value={form.price || 0} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) })} className={inp} /></Field>
              <Field label="File Type / Description"><input data-testid="pf-file-type" placeholder={form.type === "ebook" ? "PDF · 80 pages" : form.type === "merch" ? "Cotton blend" : "PBIX + Excel"} value={form.file_type || ""} onChange={(e) => setForm({ ...form, file_type: e.target.value })} className={inp} /></Field>
            </div>
          )}

          <UploadRow label="Cover Image / Mockup" field="cover_image_url" form={form} setForm={setForm} upload={upload} uploading={uploading} accept="image/*" />

          {form.type === "music" && (
            <>
              <UploadRow label="Preview Audio (MP3)" field="preview_audio_url" form={form} setForm={setForm} upload={upload} uploading={uploading} accept="audio/*" />
              <UploadRow label="Full MP3 File" field="file_mp3_url" form={form} setForm={setForm} upload={upload} uploading={uploading} accept="audio/mpeg" />
              <UploadRow label="Full WAV File" field="file_wav_url" form={form} setForm={setForm} upload={upload} uploading={uploading} accept="audio/wav" />
            </>
          )}
          {form.type === "template" && (
            <>
              <UploadRow label="Demo Video/Screenshot" field="demo_video_url" form={form} setForm={setForm} upload={upload} uploading={uploading} accept="image/*,video/*" />
              <UploadRow label="Template File (PBIX/ZIP)" field="file_template_url" form={form} setForm={setForm} upload={upload} uploading={uploading} accept=".pbix,.zip,application/zip" />
            </>
          )}
          {form.type === "ebook" && (
            <UploadRow label="E-book File (PDF)" field="file_ebook_url" form={form} setForm={setForm} upload={upload} uploading={uploading} accept=".pdf,application/pdf" />
          )}
          {form.type === "merch" && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Sizes (comma-separated)">
                <input data-testid="pf-sizes" placeholder="S, M, L, XL" value={(form.sizes || []).join(", ")} onChange={(e) => setForm({ ...form, sizes: csv(e.target.value) })} className={inp} />
              </Field>
              <Field label="Colors (comma-separated)">
                <input data-testid="pf-colors" placeholder="Black, Ivory" value={(form.colors || []).join(", ")} onChange={(e) => setForm({ ...form, colors: csv(e.target.value) })} className={inp} />
              </Field>
              <Field label="Stock Status">
                <select data-testid="pf-stock" value={form.stock_status || "in_stock"} onChange={(e) => setForm({ ...form, stock_status: e.target.value })} className={inp}>
                  <option value="in_stock">In Stock</option>
                  <option value="low_stock">Low Stock</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
              </Field>
              <Field label="External Shop URL">
                <input data-testid="pf-external-url" placeholder="https://www.hohvibesshop.com/products/..." value={form.external_url || ""} onChange={(e) => setForm({ ...form, external_url: e.target.value })} className={inp} />
              </Field>
            </div>
          )}

          <Field label="What's Included (comma-separated)">
            <input data-testid="pf-included" value={(form.whats_included || []).join(", ")} onChange={(e) => setForm({ ...form, whats_included: csv(e.target.value) })} className={inp} />
          </Field>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-ghost-gold px-5 py-2 rounded text-xs tracking-widest uppercase">Cancel</button>
            <button data-testid="pf-save" type="submit" className="btn-gold px-5 py-2 rounded text-xs tracking-widest uppercase">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}
